import { Model } from "@vuex-orm/core";
import type { Fields } from "@vuex-orm/core";
import { DirectUpload } from "@rails/activestorage";
import type { Blob as BlobType } from "@rails/activestorage";
import { buildBlobForUpload, railsActiveStorageDirectUploadsUrl } from "@modules/uploader";
import { VideoSlide } from "./ActionHierarchy";
import type { BlobActingAsFile, BlobTypeWithResponse } from "./ActionTypes";

interface Uploadable {
  id: string;
}
interface UploadableStatic {
  new (): Uploadable;
  entity: string;
}

export default class Upload extends Model implements Uploadable {
  // This is the name used as module name of the Vuex Store.
  public static entity = "uploads";

  public static fields(): Fields {
    return {
      ...super.fields(),
      url: this.attr(""),
      uploadableId: this.attr(null),
      uploadableType: this.attr(null),
      uploadable: this.morphTo("uploadableId", "uploadableType"),
      videoSlideId: this.attr(null),
      videoSlide: this.belongsTo(VideoSlide, "videoSlideId"),
      size: this.number(-1),
      uploaded: this.number(0),
      status: this.string("pending"),
      message: this.string("").nullable(),
      dataUrl: this.string("").nullable(),
      signedFileId: this.string("").nullable(),
      fileKey: this.string("").nullable(),
    };
  }

  public declare id: string;

  public declare url: string;

  public declare uploadableId: string | null;

  public declare uploadableType: string | null;

  public declare uploadable: string;

  public declare videoSlideId: string;

  public declare videoSlide: VideoSlide;

  public declare size: number;

  public declare uploaded: number;

  public declare status: "pending" | "uploading" | "failed" | "completed";

  public declare message: string | null;

  public declare dataUrl: string | null;

  public declare signedFileId: string | null;

  public declare fileKey: string | null;

  public static async for<T extends Uploadable>(uploadable: T): Promise<Upload> {
    const upload = (await Upload.new()) as Upload;
    await upload.$update({
      uploadableId: uploadable.id,
      uploadableType: (uploadable.constructor as UploadableStatic).entity,
    });
    return upload;
  }

  public perform(file: BlobActingAsFile | File): Promise<BlobTypeWithResponse> {
    // // Canvas currently not in use:
    // if ((file as HTMLCanvasElement).toDataURL) {
    //   this.$update({ dataUrl: (file as HTMLCanvasElement).toDataURL() });
    // }
    const uploadMonitor = {
      directUploadWillStoreFileWithXHR: (xhr: XMLHttpRequest): void => {
        xhr.upload.addEventListener("progress", (event) => {
          this.$update({ size: event.total, uploaded: event.loaded });
          // For reactivity
          this.size = event.total;
          this.uploaded = event.loaded;
        });
      },
    };
    // add auth https://github.com/rails/rails/issues/32208#issuecomment-406849732
    // console.log("Upload#perform() file", file);
    const directUpload = new DirectUpload(
      file as File,
      railsActiveStorageDirectUploadsUrl,
      uploadMonitor,
    );
    // console.log("Upload#perform() self", this);
    return new Promise((resolve, reject) => {
      this.$update({ status: "uploading", size: (file as Blob).size });
      // For reactivity
      this.status = "uploading";
      this.size = (file as Blob).size;

      directUpload.create((error: Error, activeStorageBlob: BlobType) => {
        if (error) {
          console.error("Upload#perform() error:", error);
          this.$update({ status: "failed", message: error });
          // For reactivity
          this.status = "failed";
          reject(error);
        } else {
          // console.debug("Upload#perform() success, blob:", blob.key);
          this.$update({
            status: "completed",
            signedFileId: (activeStorageBlob as BlobTypeWithResponse).signed_id,
            fileKey: (activeStorageBlob as BlobTypeWithResponse).key,
          });
          // For reactivity
          this.status = "completed";
          this.signedFileId = (activeStorageBlob as BlobTypeWithResponse).signed_id;
          this.fileKey = (activeStorageBlob as BlobTypeWithResponse).key;

          resolve(activeStorageBlob as BlobTypeWithResponse);
        }
      });
    });
  }

  public get progress(): number {
    if (this.size && this.size > 0) return this.uploaded / this.size;
    return 0;
  }

  public get progressPercentage(): number {
    return this.progress * 100;
  }

  public static buildBlobForUpload(dataURL: string): BlobActingAsFile {
    return buildBlobForUpload(dataURL);
  }
}
